/**
 * 📝 FEEDBACK MODULE - COMPOSANT UTILISATEUR
 * ==========================================
 * 
 * Formulaire de feedback optionnel en bas de chaque module.
 * N'influence PAS la progression.
 * 
 * Qualiopi Indicateur 32 - Recueil des appréciations
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  MessageSquare, Send, Star, CheckCircle2, 
  ThumbsUp, AlertCircle, Sparkles
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { getBackendUrl } from '../config/environment';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

/**
 * Composant Feedback Module
 */
const ModuleFeedback = ({ moduleId, moduleName }) => {
  const [canSubmit, setCanSubmit] = useState(true);
  const [feedbackText, setFeedbackText] = useState('');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [category, setCategory] = useState('general');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    checkCanSubmit();
  }, [moduleId]);

  const checkCanSubmit = async () => {
    try {
      const res = await axios.get(`${API}/feedback/module/${moduleId}/can-submit`);
      setCanSubmit(res.data.can_submit);
    } catch (err) {
      console.error('Erreur vérification feedback:', err);
    }
  };

  const handleSubmit = async () => {
    if (!feedbackText.trim() || feedbackText.length < 10) {
      toast.error('Votre feedback doit contenir au moins 10 caractères');
      return;
    }

    try {
      setSubmitting(true);
      
      await axios.post(`${API}/feedback/submit`, {
        module_id: moduleId,
        feedback_text: feedbackText.trim(),
        rating: rating || null,
        category
      });
      
      setSubmitted(true);
      toast.success('Merci pour votre feedback !');
    } catch (err) {
      console.error('Erreur soumission feedback:', err);
      toast.error('Erreur lors de l\'envoi du feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const categories = [
    { value: 'contenu', label: 'Contenu', icon: '📚' },
    { value: 'pedagogie', label: 'Pédagogie', icon: '🎓' },
    { value: 'technique', label: 'Technique', icon: '⚙️' },
    { value: 'general', label: 'Général', icon: '💬' }
  ];

  // Si déjà soumis aujourd'hui - afficher un état lecture seule
  if (!canSubmit && !submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="mt-8"
      >
        <Card className="border border-purple-200/50 bg-gradient-to-br from-purple-50/50 to-white">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-xl">
                <CheckCircle2 className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h4 className="font-semibold text-[#2B1854]">Votre avis a été enregistré</h4>
                <p className="text-sm text-gray-500 mt-1">
                  Merci pour votre contribution ! Vous pourrez soumettre un nouveau feedback demain.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  // Succès
  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-8"
      >
        <Card className="border border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
          <CardContent className="p-6 text-center">
            <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <h4 className="font-semibold text-green-700 mb-1">Merci pour votre feedback !</h4>
            <p className="text-sm text-green-600">
              Votre retour aide Owen à améliorer continuellement la formation.
            </p>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5 }}
      className="mt-8"
    >
      <Card className="border border-[#FFD86F]/20 bg-white/80 backdrop-blur-sm overflow-hidden">
        {/* Header cliquable */}
        <CardHeader 
          className="cursor-pointer hover:bg-gray-50/50 transition-colors border-b border-[#FFD86F]/10"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-[#FFD86F]/20 to-[#FFB347]/20 rounded-lg">
                <MessageSquare className="w-5 h-5 text-[#FFB347]" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold text-[#2B1854]">
                  Votre avis sur ce module
                </CardTitle>
                <p className="text-xs text-gray-500 mt-0.5">
                  Optionnel - Aide Owen à s&apos;améliorer
                </p>
              </div>
            </div>
            <motion.div
              animate={{ rotate: isExpanded ? 180 : 0 }}
              className="text-gray-400"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </motion.div>
          </div>
        </CardHeader>

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <CardContent className="p-6 space-y-5">
                {/* Note étoiles */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Votre note (optionnel)
                  </label>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <motion.button
                        key={star}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => setRating(star)}
                        onMouseEnter={() => setHoverRating(star)}
                        onMouseLeave={() => setHoverRating(0)}
                        className="p-1"
                      >
                        <Star 
                          className={`w-7 h-7 transition-colors ${
                            star <= (hoverRating || rating)
                              ? 'text-amber-400 fill-amber-400'
                              : 'text-gray-300'
                          }`}
                        />
                      </motion.button>
                    ))}
                  </div>
                </div>

                {/* Catégorie */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Catégorie
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {categories.map((cat) => (
                      <button
                        key={cat.value}
                        onClick={() => setCategory(cat.value)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                          category === cat.value
                            ? 'bg-[#3B1E73] text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {cat.icon} {cat.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Textarea */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Votre commentaire
                  </label>
                  <Textarea
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                    placeholder="Qu'avez-vous pensé de ce module ? Qu'est-ce qui vous a aidé ou pourrait être amélioré ?"
                    className="min-h-[100px] resize-none"
                    maxLength={1000}
                  />
                  <p className="text-xs text-gray-400 mt-1 text-right">
                    {feedbackText.length}/1000
                  </p>
                </div>

                {/* Submit */}
                <div className="flex justify-end">
                  <Button
                    onClick={handleSubmit}
                    disabled={submitting || feedbackText.length < 10}
                    className="gap-2 bg-gradient-to-r from-[#FFD86F] to-[#FFB347] text-[#2B1854] hover:shadow-lg"
                  >
                    {submitting ? (
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="w-4 h-4 border-2 border-current border-t-transparent rounded-full"
                      />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                    Envoyer mon feedback
                  </Button>
                </div>

                {/* Info */}
                <p className="text-xs text-gray-400 text-center">
                  <AlertCircle className="w-3 h-3 inline mr-1" />
                  Ce feedback est anonymisé et n&apos;influence pas votre progression.
                </p>
              </CardContent>
            </motion.div>
          )}
        </AnimatePresence>
      </Card>
    </motion.div>
  );
};

export default ModuleFeedback;
